/**
 * Utility functions for AddStatCardModal form
 * Dynamic validation and filtering based on datasource column metadata
 */

import type { WpFieldConfig } from "@hooks/use-config/types";
import { TableCellInputType } from "@components/data-table/types";
import { GROUPABLE_INPUT_TYPES, NON_GROUPABLE_INPUT_TYPES, getFieldTypeLabel } from "../reporting-constants";

/**
 * Metadata about a column for aggregation decisions
 */
export interface ColumnMetadata {
  id: string;
  label: string;
  type: string;
  isNumeric: boolean;
  isDate: boolean;
  isGroupable: boolean;
  hasOptions: boolean;
  options?: Array<{ value: string | number; label: string }>;
}

/**
 * Dimension requirements based on aggregation type
 */
export interface DimensionRequirement {
  count: number;
  labels: string[];
  description: string;
}

/**
 * Form validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Extract options from field config
 */
function extractOptions(
  fieldConfig?: WpFieldConfig
): Array<{ value: string | number; label: string }> | undefined {
  if (!fieldConfig) return undefined;

  // Handle regular options array: { value, label }[]
  if (fieldConfig.options && Array.isArray(fieldConfig.options)) {
    const opts = fieldConfig.options as any[];
    if (opts.length > 0 && opts[0].value !== undefined && opts[0].label !== undefined) {
      return opts as Array<{ value: string | number; label: string }>;
    }
  }

  // Handle taxonomy array: { slug, name }[]
  if (fieldConfig.taxonomy && Array.isArray(fieldConfig.taxonomy)) {
    const taxs = fieldConfig.taxonomy as any[];
    if (taxs.length > 0 && taxs[0].slug !== undefined && taxs[0].name !== undefined) {
      return taxs.map((t) => ({ value: t.slug, label: t.name }));
    }
  }

  return undefined;
}

/**
 * Get metadata about a table column for aggregation decisions
 * Inspects field config to determine capabilities
 */
export function getColumnMetadata(
  columnId: string,
  columnLabel: string,
  fieldConfig?: WpFieldConfig
): ColumnMetadata {
  const inputType = fieldConfig?.field_type as TableCellInputType | undefined;
  
  // Detect numeric fields by checking the raw WpFieldConfig type
  // Fields with type "number" or input type "number" are numeric
  const rawType = (fieldConfig?.type || fieldConfig?.field_type || "").toLowerCase();
  const isNumeric = rawType === "number" || rawType.includes("number") || 
                    (fieldConfig as any)?.inputType === "number";
  
  const isDate = inputType === TableCellInputType.Date || inputType === TableCellInputType.DateTime;

  const isGroupable = inputType
    ? !NON_GROUPABLE_INPUT_TYPES.has(inputType) && GROUPABLE_INPUT_TYPES.has(inputType)
    : true; // Default to true if no type info
  const options = extractOptions(fieldConfig);
  const hasOptions = Boolean(options && options.length > 0);

  return {
    id: columnId,
    label: columnLabel,
    type: getFieldTypeLabel(inputType),
    isNumeric,
    isDate,
    isGroupable,
    hasOptions,
    options,
  };
}

/**
 * Special column for count aggregations
 */
const COUNT_COLUMN: ColumnMetadata = {
  id: "count",
  label: "Count",
  type: "Special",
  isNumeric: true,
  isDate: false,
  isGroupable: false,
  hasOptions: false,
};

/**
 * Get dimension requirements based on aggregation type
 */
export function getDimensionRequirements(aggregationType: string): DimensionRequirement {
  switch (aggregationType) {
    case "total-items":
      return {
        count: 0,
        labels: [],
        description: "Total count of all items in this report",
      };
    case "sum":
      return {
        count: 1,
        labels: ["Field to sum"],
        description: "Select 1 numeric field to sum",
      };
    case "average":
      return {
        count: 2,
        labels: ["Numerator", "Denominator"],
        description: "Select a field to average and a field to group by",
      };
    case "count":
      return {
        count: 1,
        labels: ["Field to count"],
        description: "Select a field to count occurrences of",
      };
    case "custom":
      return {
        count: 0,
        labels: [],
        description: "Custom aggregation (advanced mode)",
      };
    default:
      return {
        count: 0,
        labels: [],
        description: "Unknown aggregation type",
      };
  }
}

/**
 * Filter columns suitable as the Nth dimension for a given aggregation type
 */
export function filterColumnsForDimension(
  allColumns: ColumnMetadata[],
  aggregationType: string,
  dimensionIndex: number
): ColumnMetadata[] {
  if (aggregationType === "total-items") {
    return [];
  }

  if (aggregationType === "sum") {
    // sum requires numeric field (typically just COUNT)
    return [COUNT_COLUMN, ...allColumns.filter((col) => col.isNumeric)];
  }

  if (aggregationType === "average") {
    if (dimensionIndex === 0) {
      // First dimension: numeric field for aggregation (typically COUNT)
      return [COUNT_COLUMN, ...allColumns.filter((col) => col.isNumeric)];
    } else {
      // Second dimension: groupable field or count (as a numeric weight)
      return [COUNT_COLUMN, ...allColumns.filter((col) => col.isGroupable)];
    }
  }

  if (aggregationType === "count") {
    // count can use any groupable field (no COUNT special)
    return allColumns.filter((col) => col.isGroupable);
  }

  return [COUNT_COLUMN, ...allColumns];
}

/**
 * Validate that selected dimensions match aggregation requirements
 */
export function validateDimensions(
  selectedDimensionIds: (string | null | undefined)[],
  allColumns: ColumnMetadata[],
  aggregationType: string
): ValidationResult {
  if (aggregationType === "total-items") {
    return { isValid: true };
  }

  const requirements = getDimensionRequirements(aggregationType);

  // Check count
  const selectedCount = selectedDimensionIds.filter((id) => id !== null && id !== undefined).length;
  if (selectedCount < requirements.count) {
    return {
      isValid: false,
      errorMessage: `${aggregationType} requires ${requirements.count} dimension(s); you have selected ${selectedCount}`,
    };
  }

  if (selectedCount > requirements.count) {
    return {
      isValid: false,
      errorMessage: `${aggregationType} requires ${requirements.count} dimension(s); you have selected ${selectedCount}`,
    };
  }

  // Validate each dimension based on aggregation type
  if (aggregationType === "sum") {
    const selectedId = selectedDimensionIds[0];
    if (selectedId && selectedId !== "count") {
      const col = allColumns.find((c) => c.id === selectedId);
      if (col && !col.isNumeric) {
        return {
          isValid: false,
          errorMessage: `${col.label} is a ${col.type} field. Sum requires a numeric field.`,
        };
      }
    }
  }

  if (aggregationType === "average") {
    const aggregateId = selectedDimensionIds[0];
    if (aggregateId && aggregateId !== "count") {
      const col = allColumns.find((c) => c.id === aggregateId);
      if (col && !col.isNumeric) {
        return {
          isValid: false,
          errorMessage: `${col.label} is a ${col.type} field. Average requires a numeric field for aggregation.`,
        };
      }
    }

    const groupId = selectedDimensionIds[1];
    if (groupId) {
      const col = allColumns.find((c) => c.id === groupId);
      if (col && !col.isGroupable) {
        return {
          isValid: false,
          errorMessage: `${col.label} is not suitable for grouping.`,
        };
      }
    }
  }

  if (aggregationType === "count") {
    const selectedId = selectedDimensionIds[0];
    if (selectedId && selectedId !== "count") {
      const col = allColumns.find((c) => c.id === selectedId);
      if (col && !col.isGroupable) {
        return {
          isValid: false,
          errorMessage: `${col.label} is not suitable for grouping.`,
        };
      }
    }
  }

  return { isValid: true };
}

/**
 * Build a description of dimensions for display/preview
 */
export function buildDimensionDescription(
  selectedDimensionIds: (string | null | undefined)[],
  allColumns: ColumnMetadata[],
  aggregationType: string
): string {
  const parts: string[] = [];

  switch (aggregationType) {
    case "total-items":
      parts.push("All Items");
      break;
    case "sum":
      if (selectedDimensionIds[0]) {
        const col = selectedDimensionIds[0] === "count" ? COUNT_COLUMN : allColumns.find((c) => c.id === selectedDimensionIds[0]);
        if (col) parts.push(`Sum of ${col.label}`);
      }
      break;
    case "average":
      if (selectedDimensionIds[0]) {
        const aggregateCol = selectedDimensionIds[0] === "count" ? COUNT_COLUMN : allColumns.find((c) => c.id === selectedDimensionIds[0]);
        if (aggregateCol) parts.push(`Average of ${aggregateCol.label}`);
      }
      if (selectedDimensionIds[1]) {
        const groupCol = selectedDimensionIds[1] === "count" ? COUNT_COLUMN : allColumns.find((c) => c.id === selectedDimensionIds[1]);
        if (groupCol) parts.push(`grouped by ${groupCol.label}`);
      }
      break;
    case "count":
      if (selectedDimensionIds[0]) {
        const countCol = selectedDimensionIds[0] === "count" ? COUNT_COLUMN : allColumns.find((c) => c.id === selectedDimensionIds[0]);
        if (countCol) parts.push(`Count of ${countCol.label}`);
      }
      break;
  }

  return parts.join(" ") || "No dimensions selected";
}
